import React, { useEffect, useState, useCallback } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

const STATUS_LABELS = {
  pending: { text: 'Pending', color: 'warning' },
  clipping: { text: 'Clipping', color: 'info' },
  clipped: { text: 'Clipped', color: 'success' },
  rejected: { text: 'Rejected', color: 'secondary' },
};

function formatDuration(sec) {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function parseMMSS(str) {
  const parts = str.split(':');
  if (parts.length === 2) {
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  }
  return parseInt(str, 10) || 0;
}

function YouTubeCuration() {
  useAuthGuard();

  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  const [curations, setCurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Clip modal state
  const [clipTarget, setClipTarget] = useState(null);
  const [stores, setStores] = useState([]);
  const [clipStoreId, setClipStoreId] = useState('');
  const [clipStart, setClipStart] = useState('0:00');
  const [clipEnd, setClipEnd] = useState('');
  const [clipMaxHeight, setClipMaxHeight] = useState(1080);
  const [clipFrameMode, setClipFrameMode] = useState('dense');
  const [clipMaxAIFrames, setClipMaxAIFrames] = useState('');
  const [clipAnalyzeAll, setClipAnalyzeAll] = useState(false);
  const [clipping, setClipping] = useState(false);
  const [clipError, setClipError] = useState('');
  const [clipSuccess, setClipSuccess] = useState('');

  // Load stores for the clip modal
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/admin/stores?limit=500`, { signal: ac.signal });
        if (res.ok) {
          const data = await res.json();
          setStores(Array.isArray(data.data) ? data.data : []);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Failed to load stores:', e);
      }
    })();
    return () => ac.abort();
  }, []);

  const loadCurations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await apiFetch(`${API_BASE_URL}/admin/youtube-curations?${params}`);
      if (res.ok) {
        const data = await res.json();
        setCurations(Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error('Failed to load curations:', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadCurations(); }, [loadCurations]);

  const handleAdd = async () => {
    setAddError('');
    setAddSuccess('');
    if (!url.trim()) {
      setAddError('Please enter a YouTube URL');
      return;
    }
    setAdding(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/youtube-curations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      if (res.status === 409) {
        setAddError('This video is already registered');
      } else if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAddError(data.error || `Failed: ${res.status}`);
      } else {
        setAddSuccess('Video added successfully');
        setUrl('');
        loadCurations();
      }
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this curation?')) return;
    try {
      await apiFetch(`${API_BASE_URL}/admin/youtube-curations/${id}`, { method: 'DELETE' });
      loadCurations();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const handleReject = async (id) => {
    try {
      await apiFetch(`${API_BASE_URL}/admin/youtube-curations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected' }),
      });
      loadCurations();
    } catch (e) {
      alert('Reject failed: ' + e.message);
    }
  };

  const openClipModal = (curation) => {
    setClipTarget(curation);
    setClipStoreId('');
    setClipStart('0:00');
    setClipEnd(formatDuration(curation.durationSec));
    setClipMaxHeight(1080);
    setClipFrameMode('dense');
    setClipMaxAIFrames('');
    setClipAnalyzeAll(false);
    setClipError('');
    setClipSuccess('');
  };

  const handleClip = async () => {
    setClipError('');
    setClipSuccess('');
    const startSec = parseMMSS(clipStart);
    const endSec = parseMMSS(clipEnd);
    if (endSec <= startSec) {
      setClipError('End time must be after start time');
      return;
    }
    setClipping(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/youtube-curations/${clipTarget.id}/clip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: clipStoreId || undefined,
          startSec,
          endSec,
          maxHeight: clipMaxHeight,
          frameMode: clipFrameMode,
          maxAIFrames: clipMaxAIFrames !== '' ? Number(clipMaxAIFrames) : undefined,
          analyzeAllFrames: clipAnalyzeAll,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setClipError(data.error || `Failed: ${res.status}`);
      } else {
        const data = await res.json();
        setClipSuccess(`Clip job created. Session: ${data.data?.sessionId?.substring(0, 8)}...`);
        loadCurations();
      }
    } catch (e) {
      setClipError(e.message);
    } finally {
      setClipping(false);
    }
  };

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">YouTube Curation</h2>
        <p className="page-subtitle">Curate YouTube videos for clip extraction and AI analysis</p>
      </div>

      {/* Add Video */}
      <div className="card mb-4">
        <div className="card-header bg-white py-2">
          <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Add Video</h6>
        </div>
        <div className="card-body">
          <div className="d-flex gap-2 align-items-start">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              style={{ maxWidth: 500 }}
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={adding}
              onClick={handleAdd}
            >
              {adding ? <span className="spinner-border spinner-border-sm" /> : 'Add'}
            </button>
          </div>
          {addError && <div className="alert alert-danger py-1 small mt-2 mb-0">{addError}</div>}
          {addSuccess && <div className="alert alert-success py-1 small mt-2 mb-0">{addSuccess}</div>}
        </div>
      </div>

      {/* Video List */}
      <div className="card">
        <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Video List</h6>
          <div className="d-flex gap-2 align-items-center">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '0.75rem' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="clipping">Clipping</option>
              <option value="clipped">Clipped</option>
              <option value="rejected">Rejected</option>
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={loadCurations} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {curations.length === 0 && !loading ? (
            <p className="text-center py-4 text-muted small">No curations yet</p>
          ) : (
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th className="ps-3 small" style={{ color: '#8a94ad', width: 80 }}>Thumb</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Title</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Channel</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Duration</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Status</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {curations.map((c) => {
                  const status = STATUS_LABELS[c.status] || { text: c.status, color: 'secondary' };
                  return (
                    <tr key={c.id}>
                      <td className="ps-3">
                        {c.thumbnailUrl ? (
                          <img
                            src={c.thumbnailUrl}
                            alt=""
                            style={{ width: 70, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          />
                        ) : (
                          <div style={{ width: 70, height: 40, background: '#dee2e6', borderRadius: 4 }} />
                        )}
                      </td>
                      <td className="small" style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.title}>
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {c.title || c.youtubeVideoId}
                        </a>
                      </td>
                      <td className="small" style={{ color: '#555' }}>{c.channel || '-'}</td>
                      <td className="small">{formatDuration(c.durationSec)}</td>
                      <td>
                        <span className={`badge bg-${status.color}`} style={{ fontSize: '0.7rem' }}>
                          {status.text}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          {c.status === 'pending' && (
                            <button
                              className="btn btn-outline-primary btn-sm py-0 px-1"
                              style={{ fontSize: '0.65rem' }}
                              onClick={() => openClipModal(c)}
                            >
                              Clip
                            </button>
                          )}
                          {c.status === 'pending' && (
                            <button
                              className="btn btn-outline-secondary btn-sm py-0 px-1"
                              style={{ fontSize: '0.65rem' }}
                              onClick={() => handleReject(c.id)}
                            >
                              Reject
                            </button>
                          )}
                          <button
                            className="btn btn-outline-danger btn-sm py-0 px-1"
                            style={{ fontSize: '0.65rem' }}
                            onClick={() => handleDelete(c.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Clip Modal */}
      {clipTarget && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setClipTarget(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, maxWidth: 640, width: '95vw',
              maxHeight: '95vh', overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* YouTube Embed */}
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
              <iframe
                src={`https://www.youtube.com/embed/${clipTarget.youtubeVideoId}?start=${parseMMSS(clipStart)}`}
                title={clipTarget.title}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            <div style={{ padding: '16px 20px' }}>
              <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>{clipTarget.title}</h6>
              <small className="text-muted d-block mb-3">{clipTarget.channel} &middot; {formatDuration(clipTarget.durationSec)}</small>

              {/* Store selector */}
              <div className="mb-3">
                <label className="form-label small fw-medium">Store (optional)</label>
                <select
                  className="form-select form-select-sm"
                  value={clipStoreId}
                  onChange={(e) => setClipStoreId(e.target.value)}
                >
                  <option value="">-- Select store --</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Time range & Resolution */}
              <div className="d-flex gap-3 mb-3">
                <div>
                  <label className="form-label small fw-medium">Start (MM:SS)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={clipStart}
                    onChange={(e) => setClipStart(e.target.value)}
                    style={{ width: 100 }}
                    placeholder="0:00"
                  />
                </div>
                <div>
                  <label className="form-label small fw-medium">End (MM:SS)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={clipEnd}
                    onChange={(e) => setClipEnd(e.target.value)}
                    style={{ width: 100 }}
                    placeholder="5:00"
                  />
                </div>
                <div>
                  <label className="form-label small fw-medium">Resolution</label>
                  <select
                    className="form-select form-select-sm"
                    value={clipMaxHeight}
                    onChange={(e) => setClipMaxHeight(Number(e.target.value))}
                    style={{ width: 110 }}
                  >
                    <option value={360}>360p</option>
                    <option value={720}>720p</option>
                    <option value={1080}>1080p</option>
                  </select>
                </div>
                <div>
                  <label className="form-label small fw-medium">Frame Mode</label>
                  <select
                    className="form-select form-select-sm"
                    value={clipFrameMode}
                    onChange={(e) => setClipFrameMode(e.target.value)}
                    style={{ width: 140 }}
                  >
                    <option value="dense">Dense (dedup)</option>
                    <option value="interval">Standard (2s)</option>
                    <option value="scene">Scene detect</option>
                  </select>
                </div>
              </div>

              {/* AI Budget Controls */}
              <div className="d-flex gap-3 mb-3 align-items-end">
                <div>
                  <label className="form-label small fw-medium">AI Frames Limit</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={clipMaxAIFrames}
                    onChange={(e) => setClipMaxAIFrames(e.target.value)}
                    placeholder="50"
                    style={{ width: 100 }}
                    min={1}
                    disabled={clipAnalyzeAll}
                  />
                  <small className="text-muted" style={{ fontSize: '0.65rem' }}>Empty = default (50)</small>
                </div>
                {API_BASE_URL.includes('staging') && (
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="analyzeAllCheck"
                      checked={clipAnalyzeAll}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (!window.confirm('Analyze ALL frames with AI (Roboflow+LLM). This may incur significant costs. Continue?')) {
                            return;
                          }
                        }
                        setClipAnalyzeAll(e.target.checked);
                      }}
                    />
                    <label className="form-check-label small" htmlFor="analyzeAllCheck">
                      Analyze All Frames
                    </label>
                  </div>
                )}
              </div>

              {clipError && <div className="alert alert-danger py-1 small mb-3">{clipError}</div>}
              {clipSuccess && <div className="alert alert-success py-1 small mb-3">{clipSuccess}</div>}

              <div className="d-flex justify-content-between">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setClipTarget(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={clipping}
                  onClick={handleClip}
                >
                  {clipping ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Processing...
                    </>
                  ) : (
                    'Clip & Analyze'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default YouTubeCuration;
